<script setup lang="ts">
import { kebabCase } from 'case-anything'
import { motion } from 'motion-v'
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'url': 'https://www.spothopperapp.com/faq',
  'name': 'SpotHopper FAQ - Everything Restaurant Owners Need to Know',
  'mainEntity': [

    {
      '@type': 'Question',
      'name': 'What is SpotHopper and how does it work for restaurants?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Grow your restaurant faster — without adding more to your plate. SpotHopper brings all your marketing under one roof: a high-performing website, SEO, automated reviews, and AI-driven campaigns that attract new guests and keep loyal ones coming back. It\'s everything you need to get discovered, fill more tables, and build your brand — all powered by a platform made exclusively for restaurants.',
      },
    },

    {
      '@type': 'Question',
      'name': 'How do I get started with SpotHopper?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Start seeing results right away. Getting started is simple — book a free demo and we\'ll show you exactly how SpotHopper can grow your restaurant. Our team walks you through setup, tailors the platform to your goals, and creates a clear plan for success from day one.',
      },
    },

    {
      '@type': 'Question',
      'name': 'How much does SpotHopper cost?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Plans that grow with your restaurant. SpotHopper offers flexible pricing designed for independent restaurant owners and multi-location groups alike — so you can scale your marketing without stretching your budget.',
      },
    },

    {
      '@type': 'Question',
      'name': 'How long does it take to set up my restaurant\'s website and marketing?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Get your new website and marketing system live in weeks, not months. Our team handles everything — from design to SEO and campaign setup — so you can start attracting new guests fast.',
      },
    },

    {
      '@type': 'Question',
      'name': 'What kind of support do I get with SpotHopper?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'You\'ll never have to go it alone. Every SpotHopper partner gets a dedicated Onboarding Specialist and Ongoing Support Agent — real people who know restaurants and make sure you\'re getting results from day one and every day after.',
      },
    },

    {
      '@type': 'Question',
      'name': 'How does SpotHopper help my restaurant attract new customers?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Turn more online searches into new diners. Your SpotHopper website is built to dominate local search — helping your restaurant show up first when hungry guests are deciding where to go. With built-in SEO, review management, and AI-powered campaigns, SpotHopper restaurants see an 85% increase in website visits within six months.',
      },
    },

    {
      '@type': 'Question',
      'name': 'How does SpotHopper improve my Google ranking and online visibility?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Show up first where diners are searching. SpotHopper websites are optimized for SEO from day one — automatically updating menus, specials, and events to boost your Google ranking. More visibility means more guests discovering your restaurant when it matters most.',
      },
    },

    {
      '@type': 'Question',
      'name': 'Can SpotHopper help me get more 5-star reviews for my restaurant?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Turn happy guests into loyal advocates. SpotHopper automatically follows up with guests after dining, encouraging positive reviews on Google and Yelp while routing private feedback to you first. Restaurants using SpotHopper earn up to twice as many 5-star reviews — strengthening your reputation and credibility.',
      },
    },

    {
      '@type': 'Question',
      'name': 'What kind of email and text marketing campaigns can SpotHopper run automatically?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Bring guests back again and again — automatically. SpotHopper\'s AI sends personalized email and text campaigns to past guests for birthdays, special offers, and rebookings. These campaigns double repeat visits compared to restaurants that don\'t use automated outreach — all running quietly in the background while you focus on your guests.',
      },
    },

    {
      '@type': 'Question',
      'name': 'Does SpotHopper connect with platforms like Google, OpenTable, or Yelp?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Make it easy for diners to find you everywhere they search. SpotHopper automatically syncs your website and menu with Google, ensuring your business info stays accurate and up to date. We also help you link out seamlessly to platforms like OpenTable, Yelp, and UberEats, so guests can make reservations or place orders directly — no integrations needed.',
      },
    },

    {
      '@type': 'Question',
      'name': 'What kind of results can I expect with SpotHopper?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'See measurable growth that impacts your bottom line. Restaurants using SpotHopper report fuller dining rooms, higher sales, and stronger reputations. On average, partners experience an 85% increase in website traffic and a 35% lift in organic search rankings within months.',
      },
    },

    {
      '@type': 'Question',
      'name': 'How many restaurants use SpotHopper today?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Join a thriving community of restaurant success stories. More than 18,000 restaurants nationwide rely on SpotHopper — from local favorites to fine dining destinations — with a 99% retention rate that proves our platform delivers lasting results.',
      },
    },

    {
      '@type': 'Question',
      'name': 'What makes SpotHopper different from other restaurant marketing tools?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'One platform that does what five others can\'t. Unlike generic marketing software, SpotHopper is purpose-built for restaurants — combining website design, SEO, reviews, and AI automation in one system. No more juggling vendors or tools — just one platform that drives real growth and saves you time.',
      },
    },

    {
      '@type': 'Question',
      'name': 'Why do 99% of SpotHopper customers stay with the platform year after year?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Because results this good are hard to leave. SpotHopper delivers measurable ROI, time savings, and peace of mind. With automated AI marketing and dedicated support, restaurants see SpotHopper not as a cost — but as the growth engine behind their success.',
      },
    },

    {
      '@type': 'Question',
      'name': 'Do you have customer success stories or case studies I can see?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Real restaurants, real results. From Chef Kevin Gillespie\'s Gunshow to Crawfish Shack Seafood, restaurants of all sizes have grown their online presence and filled more seats using SpotHopper. Explore our case studies to see what success could look like for your restaurant.',
      },
    },

    {
      '@type': 'Question',
      'name': 'How does SpotHopper use AI to help my restaurant grow?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Save hours each week while filling more seats. SpotHopper\'s restaurant-specific AI automates your marketing — sending the right offers, boosting Google visibility, and tailoring promotions to fill slow nights. It\'s like having a marketing team that works 24/7 — without the payroll.',
      },
    },

    {
      '@type': 'Question',
      'name': 'Is my restaurant\'s data safe with SpotHopper?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Your guest data stays yours — always. SpotHopper uses enterprise-grade encryption and strict security standards to protect your business and customer information. We never sell or share data — we simply use it to help your restaurant grow.',
      },
    },

    {
      '@type': 'Question',
      'name': 'What kinds of AI automations does SpotHopper handle for me?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Keep your restaurant visible and full — without extra work. SpotHopper automates your email and text campaigns, review requests, and website updates, ensuring every part of your marketing runs smoothly and consistently.',
      },
    },

    {
      '@type': 'Question',
      'name': 'Do I need technical skills to use SpotHopper\'s tools?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'No tech skills needed — just your passion for hospitality. Our team sets up your website, automations, and AI campaigns, while you personalize your content and review your site draft. Together, we make sure your onboarding is smooth, fast, and tailored to your goals.',
      },
    },

    {
      '@type': 'Question',
      'name': 'What is SpotHopper\'s mission and why do restaurants trust you?',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': 'Independent restaurants deserve the same marketing power as big chains — that\'s what drives us. SpotHopper helps level the playing field with AI-powered tools and hands-on support built exclusively for restaurants. That\'s why 18,000+ restaurants nationwide trust us to power their growth.',
      },
    },

  ],
}

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/faq`,
  }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(faqSchema),
    },
  ],
})

useSeoMeta({
  title: t('faq.meta.title'),
  description: t('faq.meta.description'),
  ogTitle: t('faq.meta.title'),
  ogDescription: t('faq.meta.description'),
  ogImage: `${S3_ASSETS_URL}/meta-default.jpg`,
})

// default first item in each section open
const initialActiveItems = Object.keys(tm('faq.timeline') || {}).map(
  (_, i) => `${getSectionId(rt(tm('faq.timeline')[i].title))}-0`,
)

const activeItems = ref<string[]>(initialActiveItems)
const sectionElements = useTemplateRef('section')
const { y: windowY } = useWindowScroll({ behavior: 'smooth' })

function getSectionId(title: string) {
  return kebabCase(title, { keep: ['spothopper'] })
}

function getRestaurantAvatar(name: string) {
  return `${S3_ASSETS_URL}/faq/avatars/${kebabCase(name)}.jpg`
}

function getRestaurantWebsite(name: string) {
  return match(name)
    .with('Bullpen BBQ & Taphouse', () => 'https://bullpenbbqandtaphouse.com/')
    .with('Over Yonder Restaurant', () => 'https://overyonderpilotpoint.com/')
    .with('The Euro Bistro', () => 'https://bistro-euro.com/')
    .with('Riverhouse Bistro', () => 'https://riverhousebistrosequim.com/')
    .with('Trattoria A’Vucchella', () => 'https://avucchella.com/')
    .with('Cubby’s', () => 'https://cubbysnyc.com/')
    .with('Laura’s Tea Room', () => 'https://laurastearoom.com/')
    .with('Locals Tavern', () => 'https://localstavern.com/')
    .with('Ponto Gaucho Brazilian Steakhouse', () => 'https://pontogaucho.com/')
    .with('McKoy’s Smokehouse & Saloon', () => 'https://mckoys.net/')
    .with('North Harbor Club', () => 'https://www.northharborclub.com/')
    .with('Marley’s Gotham Grill', () => 'https://marleysgothamgrill.net/')
    .with('Baka Umai', () => 'https://bakaumai.com/')
    .with('Grandpa Tony’s', () => 'https://grandpatonysli.com/')
    .with('OPUS Prime Steakhouse', () => 'https://opusprimesteakhouse.com/')
    .with('Buenos Sabores', () => 'https://buenossabores.co/')
    .with('The Stones Common House & Kitchen', () => 'https://www.thestonescommonhouse.com/')
    .with('Julie’s Deli', () => 'https://juliesdeli.com/')
    .with('Goodfolks', () => 'https://goodfolksgtx.com/')
    .with('Memories Bar & Grill', () => 'https://memoriesbargrill.com/')
    .run()
}

function getIconName(index: StringifiedInt<IntRange<0, 5>>) {
  return match(index)
    .with('0', () => 'rocket-outline' satisfies IconName)
    .with('1', () => 'star-outline' satisfies IconName)
    .with('2', () => 'chart-ascending-outline' satisfies IconName)
    .with('3', () => 'sparks-solid' satisfies IconName)
    .with('4', () => 'spothopper-outline' satisfies IconName)
    .exhaustive() as IconName
}

function isItemActive(title: string, index: string) {
  return activeItems.value.includes(`${getSectionId(title)}-${index}`)
}

function handleScrollToSection(timelineIndex: StringifiedInt<IntRange<0, 5>>) {
  if (!sectionElements.value?.length) return

  const target = sectionElements.value[Number(timelineIndex)]

  // 72px - header height
  // 72px - faq navbar
  windowY.value = window.scrollY + target.getBoundingClientRect().y - (72 * 2)
}
</script>

<template>
  <div class="isolate">
    <LayoutNavbar />

    <div class="relative overflow-clip px-4 py-12 lg:px-8 lg:py-24">
      <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
        <img
          :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
          class="aspect-video size-full object-cover object-center"
          role="presentation"
        >
      </div>

      <!-- Updated H1 and subhead -->
      <div class="isolate mx-auto grid max-w-screen-lg gap-y-5 text-balance text-center text-white">
        <h1 class="text-balance text-3xl font-medium lg:text-5xl">
          {{ t('faq.title') }}
        </h1>

        <p class="text-white/80">
          {{ t('faq.description') }}
        </p>
      </div>
    </div>

    <div class="mb-8 bg-white shadow-2xl shadow-black/10 lg:mb-16 lg:rounded-b-[5rem]">
      <div class="sticky top-[4.5rem] z-50 bg-white p-4 lg:top-[4.75rem]">
        <div class="no-scrollbar overflow-x-auto [mask:linear-gradient(to_right,transparent,black_10%_90%,transparent)]">
          <div class="mx-auto flex min-w-0 max-w-max gap-x-2 lg:gap-x-4">
            <button
              v-for="(timelineItem, timelineIndex) in tm('faq.timeline')"
              :key="timelineIndex"
              type="button"
              class="inline-flex shrink-0 items-center justify-center gap-x-2 rounded-lg bg-[#F8F8F0] px-4 py-2.5 text-sm text-baltic-900 transition-colors hover:bg-stone-200 lg:text-base"
              @click="handleScrollToSection(timelineIndex)"
            >
              <UiIcon
                :name="getIconName(timelineIndex)"
                class="text-xl"
              />

              {{ rt(timelineItem.title).replace('& Big Picture', '') }}
            </button>
          </div>
        </div>
      </div>

      <div class="isolate mx-auto mt-6 max-w-screen-lg px-4 lg:mt-12 lg:px-8">
        <div
          v-for="(timelineItem, timelineIndex) in tm('faq.timeline')"
          :key="timelineIndex"
          ref="section"
          class="grid grid-cols-[min-content_1fr] place-items-center gap-5"
        >
          <div
            :class="['relative size-full after:absolute after:left-1/2 after:z-[-1] after:h-full after:-translate-x-1/2 after:border-r after:border-dashed after:border-baltic-200 lg:order-none lg:row-span-1', {
              'after:hidden': Number(timelineIndex) === Object.keys(tm('faq.timeline')).length - 1
            }]"
          >
            <span class="inline-flex size-10 items-center justify-center rounded-[50%] bg-orange-500 text-xl text-white ring-4 ring-orange-400/30">
              <UiIcon
                :name="getIconName(timelineIndex)"
                class="text-xl"
              />
            </span>
          </div>

          <div class="grid w-full">
            <!-- Category headers use h2 -->
            <h2 class="text-xl font-medium lg:text-3xl">
              {{ rt(timelineItem.title) }}
            </h2>

            <MotionConfig
              :transition="{
                type: 'spring',
                bounce: 0.2,
                visualDuration: 0.4
              }"
            >
              <AccordionRoot
                v-model="activeItems"
                type="multiple"
                :unmount-on-hide="false"
                class="flex w-full flex-col gap-y-5 py-8"
              >
                <AccordionItem
                  v-for="(item, itemIndex) in tm(`faq.timeline.${timelineIndex}.items`)"
                  :key="itemIndex"
                  class="relative rounded-2xl ring-1 ring-transparent transition-[background-color,box-shadow] data-[state=closed]:bg-white data-[state=open]:bg-[#F9F9F2] data-[state=open]:ring-baltic-100 hover:data-[state=closed]:bg-[#F9F9F2]"
                  :value="`${getSectionId(timelineItem.title)}-${itemIndex}`"
                >
                  <AccordionHeader>
                    <AccordionTrigger as-child>
                      <motion.button
                        type="button"
                        class="group flex w-full origin-left items-center gap-x-4 rounded-2xl p-4 text-left text-lg font-medium data-[state=closed]:text-baltic-600 data-[state=open]:text-baltic-900 lg:text-xl"
                        while-press="pressed"
                        :variants="{ pressed: { scale: 0.98 } }"
                      >
                        <AnimatePresence mode="wait">
                          <motion.span
                            v-if="!isItemActive(timelineItem.title, itemIndex)"
                            :initial="{ opacity: 0, scale: 0.5, transition: { duration: 0.1 } }"
                            :animate="{ opacity: 1, scale: 1, transition: { duration: 0.2 } }"
                            :exit="{ opacity: 0, scale: 0.5, transition: { duration: 0.1 } }"
                            class="inline-flex size-8 shrink-0 items-center justify-center rounded-[50%] bg-[#F9F9F2] transition-colors group-hover:bg-white group-data-[state=open]:bg-white lg:size-10"
                          >
                            <UiIcon name="addition-solid" />
                          </motion.span>

                          <motion.span
                            v-else
                            :initial="{ opacity: 0, scale: 0.5, transition: { duration: 0.1 } }"
                            :animate="{ opacity: 1, scale: 1, transition: { duration: 0.2 } }"
                            :exit="{ opacity: 0, scale: 0.5, transition: { duration: 0.1 } }"
                            class="inline-flex size-8 shrink-0 items-center justify-center rounded-[50%] bg-[#F9F9F2] group-data-[state=open]:bg-white lg:size-10"
                          >
                            <UiIcon name="subtract-solid" />
                          </motion.span>
                        </AnimatePresence>

                        <!-- Individual questions use h3 -->
                        <h3 class="text-lg font-medium lg:text-xl">
                          {{ item.question }}
                        </h3>
                      </motion.button>
                    </AccordionTrigger>
                  </AccordionHeader>

                  <AccordionContent
                    force-mount
                    as-child
                  >
                    <motion.div
                      :variants="{
                        open: {
                          height: 'auto',
                        },
                        closed: {
                          height: 0,
                        },
                      }"
                      :initial="isItemActive(timelineItem.title, itemIndex) ? 'open' : 'closed'"
                      :animate="isItemActive(timelineItem.title, itemIndex) ? 'open' : 'closed'"
                      :transition="{ duration: 0.2, ease: 'easeOut' }"
                      style="overflow: hidden;"
                    >
                      <div class="pb-4 pl-5 pr-4 lg:pl-[4.5rem]">
                        <p>
                          {{ item.answer }}
                        </p>

                        <div class="mt-5 flex items-center gap-x-4 rounded-xl bg-neutral-200/60 p-2">
                          <div class="size-14 shrink-0 self-start overflow-clip rounded-full bg-white ring-1 ring-neutral-500/20">
                            <img
                              :src="getRestaurantAvatar(item.feedback.author_placeholder)"
                              class="size-full object-cover"
                              draggable="false"
                            >
                          </div>

                          <blockquote class="text-base">
                            <p class="text-balance font-semibold before:inline-block before:content-['“'] after:inline-block after:content-['”']">
                              {{ item.feedback.comment }}
                            </p>

                            <p class="mt-1 text-sm">
                              <i18n-t
                                :keypath="`faq.timeline.${timelineIndex}.items.${itemIndex}.feedback.author`"
                                tag="p"
                                class="text-balance"
                              >
                                <template #author_placeholder>
                                  <strong class="bold">
                                    <a
                                      :href="getRestaurantWebsite(item.feedback.author_placeholder)"
                                      class="font-semibold text-orange-500 underline underline-offset-2 transition-colors hover:bg-orange-500/10 hover:text-orange-600"
                                      target="_blank"
                                      rel="nofollow noopener"
                                    >
                                      {{ item.feedback.author_placeholder }}
                                    </a>
                                  </strong>
                                </template>
                              </i18n-t>
                            </p>
                          </blockquote>
                        </div>
                      </div>
                    </motion.div>
                  </AccordionContent>
                </AccordionItem>
              </AccordionRoot>
            </MotionConfig>
          </div>
        </div>
      </div>

      <section class="isolate max-w-screen-xl lg:mx-auto lg:w-full lg:px-4 lg:pb-20">
        <div class="relative grid place-items-center overflow-clip bg-baltic-900/90 lg:grid-cols-2 lg:rounded-3xl lg:bg-baltic-900">
          <div class="grid w-full place-items-center gap-y-5 px-8 py-8 text-center text-white lg:place-items-start lg:pl-16 lg:text-left">
            <h3 class="text-2xl font-medium lg:text-3xl">
              {{ t('faq.cta_banner.title') }}
            </h3>

            <p class="text-base text-white/70 lg:text-lg">
              {{ t('faq.cta_banner.description') }}
            </p>

            <UiButton
              class="group w-full max-w-xs lg:max-w-max"
              color="accent"
              as-child
            >
              <RouterLink :to="{ name: '/free-demo' }">
                {{ t('faq.cta_banner.action') }}

                <UiIcon
                  name="arrow-forward-rounded"
                  class="transition-transform duration-200 group-hover:translate-x-1"
                />
              </RouterLink>
            </UiButton>

            <p class="text-base text-white lg:text-lg">
              {{ t('faq.cta_banner.description_sequel') }}
            </p>
          </div>

          <div class="absolute -z-10 size-full opacity-80 after:absolute after:inset-0 after:hidden after:bg-gradient-to-l after:from-transparent after:from-30% after:to-baltic-900/90 after:to-90% lg:relative lg:z-0 lg:[mask:linear-gradient(to_right,transparent,black_30%_85%)] lg:after:block">
            <img
              :src="`${S3_ASSETS_URL}/faq/cta.jpg`"
              class="aspect-video size-full object-cover object-top lg:object-right"
              role="presentation"
            >
          </div>
        </div>
      </section>
    </div>

    <LayoutFooter />
  </div>
</template>
